import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pencil, Plus, Layout, Lock } from 'lucide-react'
import { Button } from '@trafiflow/shared-ui'
import { FunnelBadge } from '../shared/FunnelBadge'
import type { FunnelStep, ExperienceType } from '../../types'

interface OfferPagesEditorProps {
  funnelId: string
  steps: FunnelStep[]
  onAddStep?: () => void
}

type StepTab = 'upsells' | 'downsells' | 'fallbacks'

const TAB_MAP: Record<StepTab, ExperienceType | 'upsell'> = {
  upsells: 'upsell',
  downsells: 'downsell',
  fallbacks: 'fallback',
}

const TAB_LABELS: Record<StepTab, string> = {
  upsells: 'Upsells',
  downsells: 'Downsells',
  fallbacks: 'Fallbacks',
}

function StepPageCard({
  step,
  funnelId,
  isMain,
}: {
  step: FunnelStep
  funnelId: string
  isMain?: boolean
}) {
  const navigate = useNavigate()

  return (
    <div
      className="rounded-lg border p-4 hover:shadow-sm transition-shadow"
      style={{
        borderColor: 'var(--fn-border)',
        backgroundColor: 'var(--fn-bg-card)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isMain && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-700">
              MAIN
            </span>
          )}
          <FunnelBadge type="step" value={step.stepType} />
        </div>
        <div className="flex items-center gap-1">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: step.pageContent ? '#22c55e' : '#94a3b8' }}
          />
          <span className="text-xs text-fn-text-muted">
            {step.pageContent ? 'Has page' : 'No page'}
          </span>
        </div>
      </div>

      <h4 className="text-sm font-medium mb-1 text-fn-text">
        {step.name || `Step ${step.position + 1}`}
      </h4>

      {step.offerId && (
        <p className="text-xs mb-3 text-fn-text-muted">
          Offer: {step.offerId}
        </p>
      )}

      <Button
        variant="secondary"
        className="w-full text-sm"
        onClick={() => navigate(`/funnels/${funnelId}/steps/${step.id}/builder`)}
      >
        <Pencil className="w-3.5 h-3.5 mr-1.5" />
        Edit Page
      </Button>
    </div>
  )
}

function AddStepCard({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border-2 border-dashed border-fn-border p-4 flex flex-col items-center justify-center min-h-[140px] cursor-pointer hover:border-blue-400 transition-colors w-full text-left"
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-fn-hover"
      >
        <Plus className="w-5 h-5 text-fn-text-muted" />
      </div>
      <p className="text-sm font-medium text-fn-text-muted">
        Add Step
      </p>
    </button>
  )
}

export function OfferPagesEditor({ funnelId, steps, onAddStep }: OfferPagesEditorProps) {
  const [activeTab, setActiveTab] = useState<StepTab>('upsells')

  const filteredSteps = steps.filter((step) => {
    const targetType = TAB_MAP[activeTab]
    if (step.experienceType) return step.experienceType === targetType
    // Fallback: map stepType to tab
    if (activeTab === 'upsells') return step.stepType === 'upsell'
    if (activeTab === 'downsells') return step.stepType === 'downsell'
    return step.stepType === 'checkout'
  })

  return (
    <div className="space-y-6">
      {/* Edit Offer Pages section */}
      <div
        className="rounded-lg border border-fn-border bg-fn-card"
      >
        <div className="px-4 py-3 border-b border-fn-border">
          <div className="flex items-center gap-2">
            <Layout className="w-4 h-4 text-fn-primary" />
            <h3 className="text-sm font-semibold text-fn-text">
              Edit Offer Pages
            </h3>
          </div>
          <p className="text-xs mt-1 text-fn-text-muted">
            Design and customize the pages shown to customers at each funnel step
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-fn-border">
          {(Object.keys(TAB_LABELS) as StepTab[]).map((tab) => {
            const count = steps.filter((s) => {
              if (s.experienceType) return s.experienceType === TAB_MAP[tab]
              if (tab === 'upsells') return s.stepType === 'upsell'
              if (tab === 'downsells') return s.stepType === 'downsell'
              return s.stepType === 'checkout'
            }).length
            return (
              <button
                key={tab}
                type="button"
                className={`px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === tab ? 'text-fn-primary' : 'text-fn-text-sub'}`}
                style={{
                  borderBottom: activeTab === tab ? '2px solid var(--fn-primary)' : '2px solid transparent',
                }}
                onClick={() => setActiveTab(tab)}
              >
                {TAB_LABELS[tab]}
                {count > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-tf-bg-hover text-tf-text-muted">
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Step cards grid */}
        <div className="p-4">
          {filteredSteps.length === 0 && (
            <p className="text-sm text-center py-6 text-fn-text-muted">
              No {TAB_LABELS[activeTab].toLowerCase()} steps yet. Add one to get started.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSteps.map((step, index) => (
              <StepPageCard
                key={step.id}
                step={step}
                funnelId={funnelId}
                isMain={index === 0}
              />
            ))}
            <AddStepCard onClick={onAddStep} />
          </div>
        </div>
      </div>

      {/* Landing Pages & Product Details — Coming Soon */}
      <div
        className="rounded-lg border p-6"
        style={{
          borderColor: 'var(--fn-border)',
          backgroundColor: 'var(--fn-bg-card)',
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center bg-fn-hover"
          >
            <Lock className="w-5 h-5 text-fn-text-muted" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-fn-text">
                Landing Pages & Product Details
              </h3>
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-tf-feedback-warning-bg text-tf-feedback-warning-text">
                Coming Soon
              </span>
            </div>
            <p className="text-xs mt-1 text-fn-text-muted">
              Design custom landing pages and product detail pages for your funnels
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
